import { debounce } from 'widgets/toolbox/debounce';

/**
 * @description Base ModalQuickBuy implementation
 * @param GlobalModal Base widget for extending
 * @returns ModalQuickBuy class
 */
function ModalQuickBuyClassCreator(GlobalModal: import('widgets/global/GlobalModal').TGlobalModal) {
    /**
     * @category widgets
     * @subcategory plugin_quickbuy
     * @class ModalQuickBuy
     * @augments GlobalModal
     * @classdesc Quick buy popup implementation based on GlobalModal component.
     * It is similar to QuickView but has distinctive visual differences from regular Dialogs.
     */
    class ModalQuickBuy extends GlobalModal {
        isQuickBuy: boolean | undefined;

        resizeHandler: Array<(() => void)> | undefined;

        /**
         * @description Before open modal callback
         * @param modalData - modal data
         */
        beforeOpenModal(modalData) {
            super.beforeOpenModal(modalData);

            if (modalData.type === 'quickbuy') {
                this.isQuickBuy = true;
                this.setDialogPosition(modalData.trigger);
                this.resizeHandler = this.ev('resize', debounce(this.setDialogPosition.bind(this, modalData.trigger), 200), window);
            }
        }

        /**
         * @description On after modal close callback
         */
        onAfterCloseModal() {
            super.onAfterCloseModal();

            if (this.isQuickBuy) {
                this.isQuickBuy = false;
                this.clearDialogPosition();

                if (this.resizeHandler) {
                    this.resizeHandler.forEach(fn => fn());
                }
            }
        }

        /**
         * @description Sets dialog position handler
         * @param trigger - opening dialog trigger
         */
        setDialogPosition(trigger) {
            const tileCoordinates = trigger.getBoundingClientRect();
            const tileHorizontalExpand = 16;
            const tileWidth = tileCoordinates.width;
            const dialogWidth = tileWidth + tileHorizontalExpand * 2;
            const dialogPositionTop = 'top:' + (tileCoordinates.top + window.scrollY) + 'px;';
            const dialogPositionLeft = 'left:' + (tileCoordinates.left - tileHorizontalExpand) + 'px;';

            this.ref('container').attr('style', dialogPositionTop + dialogPositionLeft + 'width: ' + dialogWidth + 'px');
        }

        /**
         * @description Clear dialog position handler
         */
        clearDialogPosition() {
            this.ref('container').attr('style', false);
        }
    }

    return ModalQuickBuy;
}

export type TModalQuickBuy = ReturnType<typeof ModalQuickBuyClassCreator>;

export type TModalQuickBuyInstance = InstanceType<TModalQuickBuy>;

export default ModalQuickBuyClassCreator;
